import styled from "styled-components";

const colors = {
  primary: "#203b34",
  primaryLight: "#2d5347",
  secondary: "#f9ae17",
  terciary: "#E03A85",
  white: "#ffffff",
  gray: "#6b7280",
  grayLight: "#e5e7eb",
  grayBg: "#f9fafb",
  dark: "#1f2937",
  pending: "#9e9e9e",
  draft: "#2196f3",
  confirm: "#03a9f4",
  toAuto: "#ff9800",
  autoEval: "#ffc107",
  toEval: "#ff5722",
  evaluated: "#4caf50",
  notified: "#8bc34a",
};

export const StatsContainer = styled.div`
  background: linear-gradient(
    135deg,
    ${colors.primary} 0%,
    ${colors.primaryLight} 100%
  );
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 30px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    right: -10%;
    width: 400px;
    height: 400px;
    background: rgba(249, 174, 23, 0.1);
    border-radius: 50%;
    z-index: 0;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -30%;
    left: -5%;
    width: 300px;
    height: 300px;
    background: rgba(249, 174, 23, 0.05);
    border-radius: 50%;
    z-index: 0;
  }
`;

export const StatsContent = styled.div`
  position: relative;
  z-index: 1;
`;

export const StatsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

export const StatsTitle = styled.h2`
  color: ${colors.white};
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;

  i {
    color: ${colors.secondary};
    font-size: 1.5rem;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

export const StatsYear = styled.div`
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  padding: 10px 20px;
  border-radius: 24px;
  color: ${colors.white};
  font-weight: 600;
  font-size: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
    border-color: ${({ $color }) => $color || colors.secondary};
  }
`;

export const StatCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

export const StatCardIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ $color }) => `${$color}20` || `${colors.secondary}20`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $color }) => $color || colors.secondary};
  font-size: 1.25rem;
  transition: all 0.3s ease;

  ${StatCard}:hover & {
    transform: scale(1.1) rotate(5deg);
  }
`;

export const StatCardValue = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: ${colors.dark};
  line-height: 1;
`;

export const StatCardLabel = styled.div`
  font-size: 0.8rem;
  color: ${colors.gray};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 8px;
  line-height: 1.2;
`;

export const StatCardPercentage = styled.div`
  font-size: 0.8rem;
  color: ${({ $color }) => $color || colors.secondary};
  font-weight: 700;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;

  i {
    font-size: 0.7rem;
  }
`;

export const ProgressSection = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

export const ProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

export const ProgressTitle = styled.h3`
  color: ${colors.dark};
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    color: ${colors.secondary};
  }
`;

export const ProgressPercentage = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  color: ${colors.primary};
`;

export const ProgressBarContainer = styled.div`
  width: 100%;
  height: 32px;
  background: ${colors.grayLight};
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
`;

export const ProgressBarFill = styled.div`
  height: 100%;
  background: linear-gradient(
    90deg,
    ${colors.secondary} 0%,
    ${colors.primary} 100%
  );
  border-radius: 16px;
  width: ${({ $percentage }) => $percentage}%;
  transition: width 1s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 12px;
  box-shadow: 0 2px 8px rgba(249, 174, 23, 0.4);

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: shimmer 2s infinite;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

export const ProgressBarText = styled.span`
  color: ${colors.white};
  font-size: 0.75rem;
  font-weight: 700;
  position: relative;
  z-index: 1;
`;

export const StatusBreakdown = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  margin-top: 20px;
`;

export const StatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: ${colors.grayBg};
  border-radius: 8px;
  border-left: 4px solid ${({ $color }) => $color || colors.gray};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ $color }) => `${$color}15` || `${colors.gray}15`};
    transform: translateX(4px);
  }
`;

export const StatusDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ $color }) => $color || colors.gray};
  flex-shrink: 0;
  box-shadow: 0 0 8px ${({ $color }) => `${$color}80` || `${colors.gray}80`};
`;

export const StatusInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const StatusLabel = styled.div`
  font-size: 0.75rem;
  color: ${colors.gray};
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StatusCount = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: ${colors.dark};
`;

export const AlertSection = styled.div`
  margin-top: 20px;
`;

export const AlertCard = styled.div`
  background: ${({ $type }) =>
    $type === "warning"
      ? "linear-gradient(135deg, #fff3cd 0%, #fff8e1 100%)"
      : "linear-gradient(135deg, #d1ecf1 0%, #e7f5f8 100%)"};
  border-left: 4px solid
    ${({ $type }) => ($type === "warning" ? colors.toEval : colors.toAuto)};
  border-radius: 8px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 12px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }
`;

export const AlertIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ $type }) =>
    $type === "warning" ? colors.toEval : colors.toAuto};
  color: ${colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
`;

export const AlertContent = styled.div`
  flex: 1;
`;

export const AlertTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 700;
  color: ${colors.dark};
  margin-bottom: 4px;
`;

export const AlertText = styled.div`
  font-size: 0.8rem;
  color: ${colors.gray};
  font-weight: 500;
`;

export const QuickActionButton = styled.button`
  background: ${colors.white};
  color: ${({ $type }) =>
    $type === "warning" ? colors.toEval : colors.toAuto};
  border: 2px solid
    ${({ $type }) => ($type === "warning" ? colors.toEval : colors.toAuto)};
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${({ $type }) =>
      $type === "warning" ? colors.toEval : colors.toAuto};
    color: ${colors.white};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.98);
  }
`;
